import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import './styles/CustomerAvatar.css';

// Camera Capture Component for Desktop
export const CameraCapture = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false
        });

        if (mounted) {
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        }
      } catch (err) {
        console.error('Camera access error:', err);
        if (mounted) {
          setError('Unable to access camera. Please check permissions.');
        }
      }
    };

    startCamera();

    return () => {
      mounted = false;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob((blob) => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      onCapture(blob);
    }, 'image/jpeg', 0.9);
  };

  const handleCancel = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onCancel();
  };

  return (
    <div className="camera-capture-modal">
      <div className="camera-overlay" onClick={handleCancel}>
        <div className="camera-container" onClick={e => e.stopPropagation()}>
          <h3>Take a Photo</h3>

          {error ? (
            <div className="camera-error">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
            </div>
          ) : (
            <div className="camera-preview">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
              />
            </div>
          )}

          <div className="camera-actions">
            <button className="btn-cancel" onClick={handleCancel}>
              <i className="fas fa-times"></i>
              Cancel
            </button>
            {!error && (
              <button className="btn-capture" onClick={capturePhoto}>
                <i className="fas fa-camera"></i>
                Capture Photo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ImageCropper = ({ imageFile, onCrop, onCancel, existingImageUrl = null }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, size: 200 });
  const cropRef = useRef({ x: 0, y: 0, size: 200 }); // live ref — no re-renders during drag
  const [isLoaded, setIsLoaded] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, scale: 1 });
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const rafId = useRef(null);

  // Draw the canvas with crop overlay
  const drawCanvas = useCallback((img, canvas, crop) => {
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const scale = img.width / width;

    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const displayCrop = {
      x: crop.x / scale,
      y: crop.y / scale,
      size: crop.size / scale
    };

    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, width, height);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(
      displayCrop.x + displayCrop.size / 2,
      displayCrop.y + displayCrop.size / 2,
      displayCrop.size / 2,
      0,
      Math.PI * 2
    );
    ctx.fill();

    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(
      displayCrop.x + displayCrop.size / 2,
      displayCrop.y + displayCrop.size / 2,
      displayCrop.size / 2,
      0,
      Math.PI * 2
    );
    ctx.stroke();
  }, []);

  // --- Drag handlers ---
  const startDrag = useCallback((clientX, clientY) => {
    if (!isLoaded || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    const crop = cropRef.current;
    dragOffset.current = { x: x - (crop.x + crop.size / 2), y: y - (crop.y + crop.size / 2) };
    isDragging.current = true;
  }, [isLoaded]);

  const moveDrag = useCallback((clientX, clientY) => {
    if (!isDragging.current || !imageRef.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    const { size } = cropRef.current;
    const img = imageRef.current;
    cropRef.current = {
      size,
      x: Math.max(0, Math.min(x - dragOffset.current.x - size / 2, img.width - size)),
      y: Math.max(0, Math.min(y - dragOffset.current.y - size / 2, img.height - size)),
    };
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      if (canvasRef.current && imageRef.current) {
        drawCanvas(imageRef.current, canvasRef.current, cropRef.current);
      }
    });
  }, [drawCanvas]);

  const endDrag = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    setCropArea({ ...cropRef.current }); // sync ref → state (triggers slider display update)
  }, []);

  // Load and setup image with multiple URL attempts (like CustomerProfile)
  useEffect(() => {
    let mounted = true;
    
    const setupImage = async () => {
      try {
        if (imageFile) {
          // Handle file uploads (fast)
          const reader = new FileReader();
          const fileData = await new Promise((resolve) => {
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(imageFile);
          });
          
          const img = new Image();
          img.onload = () => {
            if (mounted) processLoadedImage(img);
          };
          img.src = fileData;
          
        } else if (existingImageUrl) {
          // Try multiple URL formats for existing images (same logic as CustomerProfile)
          const urlsToTry = [];
          
          // Extract Google Drive file ID and create multiple URL formats
          if (existingImageUrl.includes('/uc?id=') || existingImageUrl.includes('/d/')) {
            let fileId = null;
            
            // Extract file ID from different Google Drive URL formats
            if (existingImageUrl.includes('/uc?id=')) {
              fileId = existingImageUrl.match(/id=([^&]+)/)?.[1];
            } else if (existingImageUrl.includes('/d/')) {
              fileId = existingImageUrl.match(/\/d\/([^/]+)/)?.[1];
            }
            
            if (fileId) {
              // Add multiple Google Drive URL formats (in order of speed)
              urlsToTry.push(`https://lh3.googleusercontent.com/d/${fileId}`);
              urlsToTry.push(`https://drive.google.com/uc?export=view&id=${fileId}`);
              urlsToTry.push(`https://drive.google.com/file/d/${fileId}/view`);
            }
          }
          
          // Always add original URL as fallback
          urlsToTry.push(existingImageUrl);
          
          console.log('🖼️ Trying image URLs:', urlsToTry);
          
          // Try each URL until one works
          let imageLoaded = false;
          for (let i = 0; i < urlsToTry.length && !imageLoaded && mounted; i++) {
            try {
              console.log(`🔄 Attempting URL ${i + 1}/${urlsToTry.length}: ${urlsToTry[i]}`);
              
              const img = new Image();
              img.crossOrigin = "anonymous";
              
              const loadPromise = new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error(`Timeout loading URL ${i + 1}`));
                }, 5000); // 5 second timeout per URL
                
                img.onload = () => {
                  clearTimeout(timeout);
                  resolve(img);
                };
                
                img.onerror = () => {
                  clearTimeout(timeout);
                  reject(new Error(`Failed to load URL ${i + 1}`));
                };
              });
              
              img.src = urlsToTry[i];
              const loadedImg = await loadPromise;
              
              if (mounted) {
                console.log(`✅ Successfully loaded with URL ${i + 1}`);
                processLoadedImage(loadedImg);
                imageLoaded = true;
              }
              
            } catch (error) {
              console.log(`❌ URL ${i + 1} failed:`, error.message);
              // Continue to next URL
            }
          }
          
          if (!imageLoaded && mounted) {
            console.error('❌ All image URLs failed to load');
            alert('Unable to load image for editing. Please try uploading a new photo instead.');
            onCancel();
          }
        }
        
      } catch (error) {
        console.error('❌ Image setup failed:', error);
        if (mounted) {
          alert('Failed to load image. Please try again.');
          onCancel();
        }
      }
    };
    
    const processLoadedImage = (loadedImg) => {
      imageRef.current = loadedImg;
      
      // Calculate display dimensions
      const maxSize = 400;
      const ratio = loadedImg.width / loadedImg.height;
      const displayWidth = ratio > 1 ? maxSize : maxSize * ratio;
      const displayHeight = ratio > 1 ? maxSize / ratio : maxSize;
      
      setDimensions({
        width: displayWidth,
        height: displayHeight,
        scale: loadedImg.width / displayWidth
      });
      
      // Initial crop
      const minDim = Math.min(loadedImg.width, loadedImg.height);
      const size = Math.min(minDim * 0.8, 300);
      const initialCrop = {
        x: (loadedImg.width - size) / 2,
        y: (loadedImg.height - size) / 2,
        size
      };
      
      cropRef.current = initialCrop;
      setCropArea(initialCrop);
      setIsLoaded(true);
      
      // Setup canvas
      setTimeout(() => {
        if (mounted && canvasRef.current) {
          const canvas = canvasRef.current;
          canvas.width = displayWidth;
          canvas.height = displayHeight;
          canvas.style.width = `${displayWidth}px`;
          canvas.style.height = `${displayHeight}px`;
          drawCanvas(loadedImg, canvas, initialCrop);
        }
      }, 50); // Faster response
    };
    
    setupImage();
    return () => { mounted = false; };
  }, [imageFile, existingImageUrl, drawCanvas, onCancel]);

  // Redraw only when slider changes (drag redraws via RAF directly)
  useEffect(() => {
    if (isLoaded && canvasRef.current && imageRef.current) {
      cropRef.current = cropArea;
      drawCanvas(imageRef.current, canvasRef.current, cropArea);
    }
  }, [cropArea, isLoaded, drawCanvas]);

  // Cleanup RAF on unmount
  useEffect(() => () => { if (rafId.current) cancelAnimationFrame(rafId.current); }, []);

  const handleSizeChange = (e) => {
    if (!imageRef.current) return;
    const size = Math.max(50, Math.min(parseInt(e.target.value), Math.min(imageRef.current.width, imageRef.current.height)));
    const newCrop = {
      size,
      x: Math.max(0, Math.min(cropRef.current.x, imageRef.current.width - size)),
      y: Math.max(0, Math.min(cropRef.current.y, imageRef.current.height - size)),
    };
    cropRef.current = newCrop;
    setCropArea(newCrop);
  };

  const handleCrop = () => {
    if (!imageRef.current) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 300;
    canvas.height = 300;
    
    ctx.drawImage(
      imageRef.current,
      cropArea.x, cropArea.y, cropArea.size, cropArea.size,
      0, 0, 300, 300
    );
    
    canvas.toBlob(onCrop, 'image/jpeg', 0.9);
  };

  if (!isLoaded) {
    return (
      <div className="image-cropper-modal">
        <div className="cropper-overlay">
          <div className="cropper-container">
            <div className="cropper-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading image...</span>
              <button 
                className="btn-cancel"
                onClick={onCancel}
                style={{ marginTop: '20px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="image-cropper-modal">
      <div className="cropper-overlay" onClick={onCancel}>
        <div className="cropper-container" onClick={e => e.stopPropagation()}>
          <h3>Adjust Your Photo</h3>
          <p className="cropper-instruction">Drag the circle to position your crop</p>

          <div className="cropper-canvas-container">
            <canvas
              ref={canvasRef}
              style={{ cursor: isDragging.current ? 'grabbing' : 'grab', touchAction: 'none' }}
              onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
              onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
              onTouchStart={(e) => { e.preventDefault(); startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
              onTouchMove={(e) => { e.preventDefault(); moveDrag(e.touches[0].clientX, e.touches[0].clientY); }}
              onTouchEnd={endDrag}
            />
          </div>
          
          <div className="cropper-controls">
            <label>
              <i className="fas fa-expand-arrows-alt"></i>
              Crop Size:
              <input
                type="range"
                min="50"
                max={imageRef.current ? Math.min(imageRef.current.width, imageRef.current.height) : 300}
                value={cropArea.size}
                onChange={handleSizeChange}
              />
              <span>{Math.round(cropArea.size)}px</span>
            </label>
          </div>
          
          <div className="cropper-actions">
            <button className="btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn-crop" onClick={handleCrop}>
              <i className="fas fa-check"></i>
              Use This Photo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <div className="toast-content">
        <i className={`fas ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
        <span>{message}</span>
      </div>
      <button className="toast-close" onClick={onClose}>
        <i className="fas fa-times"></i>
      </button>
    </div>
  );
};

const CustomerAvatar = ({ customer, onAvatarUpdate, size = 'medium', clickable = true }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const sizeClasses = {
    small: 'avatar-small',
    medium: 'avatar-medium', 
    large: 'avatar-large'
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDropdown]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    if (!clickable || isUploading) return;
    setShowDropdown(!showDropdown);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image must be smaller than 5MB', 'error');
        return;
      }
      
      setSelectedFile(file);
      setShowCropper(true);
    }
    setShowDropdown(false);
    event.target.value = '';
  };

  const handleEditPhoto = () => {
    if (customer.CustomerPhotoUrl) {
      setSelectedFile(null);
      setShowCropper(true);
      setShowDropdown(false);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    const fileName = selectedFile ? selectedFile.name : 'edited_photo.jpg';
    const croppedFile = new File([croppedBlob], fileName, {
      type: 'image/jpeg',
    });
    
    uploadAvatar(croppedFile);
    setShowCropper(false);
    setSelectedFile(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedFile(null);
  };

  const handleCameraCapture = (blob) => {
    const file = new File([blob], 'camera_photo.jpg', { type: 'image/jpeg' });
    setSelectedFile(file);
    setShowCamera(false);
    setShowCropper(true);
  };

  const handleCameraCancel = () => {
    setShowCamera(false);
    setShowDropdown(false);
  };

  const uploadAvatar = async (file) => {
    try {
      setIsUploading(true);

      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be smaller than 5MB');
      }

      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post(
        `${API_URL}/api/customers/${customer.ID}/avatar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        if (onAvatarUpdate) {
          onAvatarUpdate({
            ...customer,
            CustomerPhotoUrl: response.data.photoUrl
          });
        }
        
        showToast('Avatar updated successfully! 📸', 'success');
      }

    } catch (error) {
      console.error('Avatar upload failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
      showToast(errorMessage, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const deleteAvatar = async () => {
    try {
      setIsUploading(true);
      setShowDropdown(false);

      const response = await axios.delete(
        `${API_URL}/api/customers/${customer.ID}/avatar`
      );

      if (response.data.success) {
        if (onAvatarUpdate) {
          onAvatarUpdate({
            ...customer,
            CustomerPhotoUrl: null
          });
        }
        
        showToast('Avatar removed successfully', 'success');
      }

    } catch (error) {
      console.error('Avatar deletion failed:', error);
      const errorMessage = error.response?.data?.error || 'Deletion failed';
      showToast(errorMessage, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleMenuOption = (action, e) => {
    e.stopPropagation();

    switch (action) {
      case 'upload':
        fileInputRef.current?.click();
        break;
      case 'camera':
        // Check if on mobile device
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          // Use native camera on mobile
          cameraInputRef.current?.click();
        } else {
          // Use WebRTC camera on desktop
          setShowCamera(true);
          setShowDropdown(false);
        }
        break;
      case 'edit':
        handleEditPhoto();
        break;
      case 'delete':
        deleteAvatar();
        break;
      default:
        setShowDropdown(false);
    }
  };

  return (
    <>
      <div className="customer-avatar-container" ref={dropdownRef}>
        <div 
          className={`customer-avatar ${sizeClasses[size]} ${clickable ? 'clickable' : ''} ${isUploading ? 'uploading' : ''} ${showDropdown ? 'active' : ''}`}
          onClick={handleAvatarClick}
          title={clickable ? 'Tap to change avatar' : ''}
        >
          {customer.CustomerPhotoUrl ? (
            <img 
              src={customer.CustomerPhotoUrl} 
              alt={`${customer.CustomerFullName || customer.CustomerFirstName} ${customer.CustomerLastName}`}
              className="avatar-image"
              loading="lazy"
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              onError={(e) => {
                console.log('Image failed to load:', customer.CustomerPhotoUrl);
                if (e.target.src.includes('/uc?id=')) {
                  const fileId = customer.CustomerPhotoUrl.match(/id=([^&]+)/)?.[1];
                  if (fileId) {
                    e.target.src = `https://lh3.googleusercontent.com/d/${fileId}`;
                  }
                } else {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}
            
          <div 
            className="avatar-default-icon"
            style={{ display: customer.CustomerPhotoUrl ? 'none' : 'flex' }}
          >
            <i className="fas fa-user"></i>
          </div>

          {isUploading && (
            <div className="avatar-upload-overlay">
              <div className="upload-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            </div>
          )}

        
        </div>

        {showDropdown && (
          <div className="avatar-dropdown">
            <div className="dropdown-arrow"></div>
            <div className="dropdown-content">
              <button 
                className="dropdown-option primary"
                onClick={(e) => handleMenuOption('camera', e)}
              >
                <i className="fas fa-camera"></i>
                <span>Take Photo</span>
              </button>
              
              <button 
                className="dropdown-option"
                onClick={(e) => handleMenuOption('upload', e)}
              >
                <i className="fas fa-upload"></i>
                <span>Choose Photo</span>
              </button>
              
              {customer.CustomerPhotoUrl && (
                <>
                  <button 
                    className="dropdown-option"
                    onClick={(e) => handleMenuOption('edit', e)}
                  >
                    <i className="fas fa-crop"></i>
                    <span>Edit Photo</span>
                  </button>
                  
                  <button 
                    className="dropdown-option delete"
                    onClick={(e) => handleMenuOption('delete', e)}
                  >
                    <i className="fas fa-trash"></i>
                    <span>Remove Photo</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onCancel={handleCameraCancel}
        />
      )}

      {showCropper && (
        <ImageCropper
          imageFile={selectedFile}
          existingImageUrl={!selectedFile ? customer.CustomerPhotoUrl : null}
          onCrop={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </>
  );
};

export default CustomerAvatar;